<template>
    <v-app data-rtls-product-root>
        <link
            href="https://fonts.googleapis.com/css?family=Source+Sans+Pro&display=swap"
            rel="stylesheet"
        />
        <link href="https://fonts.googleapis.com/css?family=Material+Icons" rel="stylesheet" />
        <main-navbar v-if="!hideNavbar" />
        <main-sidebar v-if="showSidebar" :calendar-options="sidebarCalendarOptions" />
        <v-main :style="contentStyle">
            <div class="v-content__wrap">
                <div id="app-container">
                    <router-view v-slot="{ Component }">
                        <transition name="fade" mode="out-in">
                            <component :is="Component" />
                        </transition>
                    </router-view>
                </div>
            </div>
        </v-main>
        <v-overlay
            v-if="showAppChrome"
            v-model="isNavigating"
            class="router-overlay"
            :z-index="1000"
            :close-on-back="false"
            :close-on-content-click="false"
        >
            <spinner-dynamic />
        </v-overlay>
        <notifications v-if="showAppChrome" :popup-timeout="popupTimeout" />
        <events-handler v-if="showAppChrome" />
        <main-footer v-if="showPlatformChrome" />
        <div v-if="showPlatformChrome" id="consent_blackbar" />
    </v-app>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { find, get } from 'lodash';
import featureFlags from '@enums/feature-flags';
import { MAINTENANCE_ROUTE_PATH } from '@/js/routes/maintenance-guard';
import { isEmbedded } from '@/js/webshell-embed/embedded';

const { reportingOnly } = featureFlags;

const store = useStore();
const route = useRoute();
const router = useRouter();

const generalConfig = computed(() => store.state.clientConfig.generalConfig);
const toggleLogic = computed(() => store.state.clientConfig.toggleLogic);
const isNavigating = computed(() => store.state.isNavigating);

const popupTimeout = computed(() => generalConfig.value.defaultNotificationTimeout);
const isMaintenanceRoute = computed(() => route.path === MAINTENANCE_ROUTE_PATH);
const showAppChrome = computed(() => !isMaintenanceRoute.value);
const embedded = computed(() => isEmbedded());
// Chrome the One Retail shell already provides around us: navbar, footer and the cookie banner
// (the shell owns consent).
const showPlatformChrome = computed(() => showAppChrome.value && !embedded.value);
const topLevelRoute = computed(() => {
    const topLevelNavPath = get(route, 'matched.0.path');
    return find(router.options.routes, { path: topLevelNavPath }) || {};
});

const routeHidesNavbar = computed(() => !!get(topLevelRoute.value, 'meta.hideNavbar', false));
const hideNavbar = computed(() => embedded.value || routeHidesNavbar.value);
// The sidebar is promo content, not platform chrome, so it shows embedded too.
const showSidebar = computed(() => !routeHidesNavbar.value && !toggleLogic.value[reportingOnly]);

const contentStyle = computed(() =>
    // 3rem here pertains to $main-sidebar-width in _variables.scss
    showSidebar.value ? 'padding-left: 3rem !important' : ''
);

const sidebarCalendarOptions = computed(() => {
    const { fullPath } = router.currentRoute.value;
    return {
        path: fullPath.split('/')[1],
        showSidebarCalendar: get(topLevelRoute.value, 'meta.isSideBarCalendarExpanded', false),
    };
});

watch(
    () => generalConfig.value.showCookieBanner,
    showCookieBanner => {
        if (
            showCookieBanner &&
            showPlatformChrome.value &&
            !document.querySelector('script[src*="osano.com"]')
        ) {
            const cookieScript = document.createElement('script');
            cookieScript.setAttribute(
                'src',
                '//cmp.osano.com/16CFuWSuPfE35eNP/9e220dc6-b4e9-4b7d-8de4-9aa0e81a60b0/osano.js'
            );
            document.body.appendChild(cookieScript);
        }
    },
    { immediate: true }
);
</script>

<style lang="scss" scoped>
@import '@style/base/_variables';
html {
    position: relative;
    min-height: 100%;
}

#app-container {
    height: 100%;
}
</style>
